<template>
    <div>
        <Breadcrumbs :type="type" :header="title" :url="url"></Breadcrumbs>
        <div class="flex items-center justify-between mb-6">
            <search-filter v-model="form.search" class="mr-4 w-full" @reset="reset">
                <template v-slot:page>
                    <select v-model="form.length" class="form-select  w-20">
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="15">15</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="75">75</option>
                        <option value="100">100</option>
                        <option value="150">150</option>
                        <option value="200">200</option>
                    </select>
                    <select v-model="form.trashed" class="form-select  w-28">
                        <option :value="null">Filter</option>
                        <option value="with">With Trashed</option>
                        <option value="only">Only Trashed</option>
                    </select>
                </template>
                <template v-slot:status>
                    <select v-model="form.status" class="form-select w-32">
                        <option :value="null">Status</option>
                        <option value="1">Active</option>
                        <option value="2">Inactive</option>
                    </select>
                </template>
            </search-filter>
            <Button :type="type" :name="title" :url="createUrl" v-if="permission.add > 0"></Button>
        </div>
        <div class="bg-white rounded-md shadow overflow-x-auto">
            <table class="w-full whitespace-nowrap">
                <thead>
                    <tr class="text-left font-bold">
                        <th class="pb-2 pt-3 px-3" width="20">Sl.</th>
                        <th class="pb-2 pt-3 px-3">Title</th>
                        <th class="pb-2 pt-3 px-3">Office</th>
                        <th class="pb-2 pt-3 px-3 text-center">Photo</th>
                        <th class="pb-2 pt-3 px-3 text-center">Status</th>
                        <th class="pb-2 pt-3 px-3 text-center">Action</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="(officeGroup, officeName) in groupedAnnouncements">
                        <tr>
                            <td colspan="6" class="bg-gray-200 font-bold px-3 py-2">
                                {{ officeName }}
                            </td>
                        </tr>
                        <tr v-for="(content, index) in officeGroup" :key="content.id"
                            class="hover:bg-gray-100 focus-within:bg-gray-100">
                            <td class="pb-1 pt-1 px-3 text-center">
                                {{ (index + announcements.from) }}
                            </td>
                            <td class="pb-2 pt-3 px-3">
                                <div class="flex items-center">
                                    <span>{{ content.title }}</span>
                                    <icon v-if="content.deleted_at" name="trash"
                                        class="flex-shrink-0 ml-2 w-3 h-3 fill-gray-400" />
                                </div>
                            </td>
                            <td class="pb-2 pt-3 px-3">
                                {{ content.queue_office }}
                            </td>
                            <td class="pb-2 pt-3 px-3 text-center">
                                <img v-if="content.photo" class="block ml-4 w-8 h-8 rounded-full" :src="content.photo" />
                            </td>

                            <td class="pb-2 pt-3 px-3 text-center">
                                <status-level :status="content.status"></status-level>
                            </td>
                            <td class="pb-2 pt-3 px-3 text-center">
                                <div class="items-center px-2" v-if="permission.edit > 0">
                                    <Link class="px-4 py-2" :href="`/backend/queue-announcements/${content.id}/edit`"
                                        tabindex="-1">
                                    <i class="fa fa-edit"></i>
                                    </Link>
                                    <button v-if="!content.deleted_at" class="px-4 py-2 text-red-600" @click="softDelete(content.id)" tabindex="-1">
                                        <i class="fa fa-trash"></i>
                                    </button>
                                    <button v-else class="px-4 py-2 text-green-600" @click="restore(content.id)" tabindex="-1">
                                        <i class="fa fa-undo"></i>
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </template>
                    <tr v-if="announcements.data.length === 0">
                        <td class="px-6 py-4 border-t" colspan="6">No Announcement found.</td>
                    </tr>
                </tbody>
            </table>
        </div>
        <pagination class="mt-6" :links="announcements.links" />
    </div>
</template>

<script>
import Breadcrumbs from '@/Shared/Breadcrumbs'
import Button from '@/Shared/Button'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination'
import SearchFilter from '@/Shared/SearchFilter'
import StatusLevel from '@/Shared/StatusLevel'
import { Head, Link } from '@inertiajs/inertia-vue3'
import mapValues from 'lodash/mapValues'
import pickBy from 'lodash/pickBy'
import throttle from 'lodash/throttle'
import { ref, toRefs, inject } from 'vue'

export default {
    components: {
        Head,
        Icon,
        Link,
        Pagination,
        SearchFilter,
        Breadcrumbs,
        StatusLevel,
        Button
    },
    layout: Layout,
    props: {
        filters: Object,
        announcements: Object,
        permission: Object,
    },
    setup(props) {
        const { filters, announcements, permission } = toRefs(props)
        const form = ref({
            search: filters.value.search,
            trashed: filters.value.trashed,
            length: filters.value.length ? filters.value.length : 15,
            status: filters.value.status,
        })
        const type = ref('index')
        const title = ref('Announcement')
        const url = ref('')
        const createUrl = ref('/backend/queue-announcements/create')
        const reset = () => {
            form.value = mapValues(form, () => null)
        }
        return {
            filters,
            announcements,
            form,
            type,
            title,
            url,
            createUrl,
            reset,
            permission
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function () {
                this.$inertia.get('/backend/queue-announcements', pickBy(this.form), { preserveState: true })
            }, 150),
        },
    },
    computed: {
        groupedAnnouncements() {
            return this.announcements.data.reduce((groups, announcement) => {
                const office = announcement.queue_office || 'Unknown Office';
                if (!groups[office]) {
                    groups[office] = [];
                }
                groups[office].push(announcement);
                return groups;
            }, {});
        },
    },
    methods: {
        softDelete(id) {
            this.$swal({
                title: 'Are you sure?',
                text: "You want to delete this announcement.",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Delete',
                cancelButtonText: 'Cancel',
            }).then((result) => {
                if (result.isConfirmed) {
                    this.$inertia.delete(`/backend/queue-announcements/${id}`);
                    this.$swal.fire('Deleted!', 'The announcement has been deleted.', 'success');
                }
            });
        },
        restore(id) {
            this.$swal({
                title: 'Are you sure?',
                text: "You want to restore this announcement.",
                icon: 'info',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Restore',
                cancelButtonText: 'Cancel',
            }).then((result) => {
                if (result.isConfirmed) {
                    this.$inertia.put(`/backend/queue-announcements/${id}/restore`);
                    this.$swal.fire('Restored!', 'The announcement has been restored.', 'success');
                }
            });
        },
    },
}
</script>
